import { z } from "zod";
import { dateOnlySchema, emailSchema, phoneSchema, timestampSchema, uuidSchema } from "./api.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";
import { invoiceDocTypeSchema, invoiceStatusSchema } from "./invoices.ts";

/**
 * INVOICE DOCUMENTS & AUTOMATION — the paper, the delivery and the run.
 *
 * Companion contract to `invoices.ts` (the documents themselves). This file
 * covers what happens *around* an issued document: the branded PDF (US-4.1),
 * sending it by email or WhatsApp with a tokenised login-free link (US-4.4),
 * the recurring retainer run (US-4.3) and the exports a CA actually asks for
 * (US-4.8).
 *
 * Every response schema here follows the house rules: no `.transform()`, no
 * reliance on `.default()`, every key always present (`null`, never omitted).
 */

// ---------------------------------------------------------------------------
// The rendered PDF
// ---------------------------------------------------------------------------

export const INVOICE_PDF_STATUSES = ["pending", "rendering", "rendered", "failed"] as const;
export type InvoicePdfStatus = (typeof INVOICE_PDF_STATUSES)[number];
export const invoicePdfStatusSchema = z.enum(INVOICE_PDF_STATUSES);

/** GET /api/v1/invoices/:id/pdf (perm `invoices:read`) → 200. 409 while a render is still pending. */
export const invoicePdfSchema = z.object({
  invoiceId: uuidSchema,
  status: invoicePdfStatusSchema,
  /** The `invoices.version` the stored bytes were rendered from — a stale PDF is re-rendered. */
  renderedVersion: z.number().int().nullable(),
  attempts: z.number().int(),
  lastError: z.string().nullable(),
  fileId: uuidSchema.nullable(),
  filename: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  /** Signed, expiring download URL — null until the first successful render. */
  downloadUrl: z.string().nullable(),
  downloadUrlExpiresAt: timestampSchema.nullable(),
  renderedAt: timestampSchema.nullable(),
  renderMs: z.number().int().nullable(),
});
export type InvoicePdfDto = z.infer<typeof invoicePdfSchema>;

/** POST /api/v1/invoices/:id/pdf (perm `invoices:write`) → 202. Queues a (re-)render. */
export const renderInvoicePdfInputSchema = z.object({ force: z.boolean().optional() }).strict();
export type RenderInvoicePdfInput = z.input<typeof renderInvoicePdfInputSchema>;

// ---------------------------------------------------------------------------
// Sending (US-4.4)
// ---------------------------------------------------------------------------

export const INVOICE_SEND_CHANNELS = ["email", "whatsapp"] as const;
export type InvoiceSendChannel = (typeof INVOICE_SEND_CHANNELS)[number];
export const invoiceSendChannelSchema = z.enum(INVOICE_SEND_CHANNELS);

export const INVOICE_SEND_STATUSES = ["queued", "sent", "failed"] as const;
export type InvoiceSendStatus = (typeof INVOICE_SEND_STATUSES)[number];
export const invoiceSendStatusSchema = z.enum(INVOICE_SEND_STATUSES);

/** The secure view link lives this long unless the caller asks for something else. */
export const INVOICE_LINK_DEFAULT_DAYS = 30;
export const INVOICE_LINK_MAX_DAYS = 180;

/**
 * POST /api/v1/invoices/:id/send (perm `invoices:send`) → 200 InvoiceSendResult.
 * A draft answers CONFLICT — only a numbered document may leave the building.
 */
export const sendInvoiceInputSchema = z
  .object({
    channel: invoiceSendChannelSchema.default("email"),
    /** Overrides the billing contacts; empty means "use the client's billing profile". */
    to: z.array(emailSchema).max(10).optional(),
    cc: z.array(emailSchema).max(10).optional(),
    subject: z.string().trim().min(1).max(200).optional(),
    message: z.string().trim().max(4000).optional(),
    /** WhatsApp only: the number to open the chat with (defaults to the billing contact). */
    phone: phoneSchema.optional(),
    attachPdf: z.boolean().default(true),
    linkExpiryDays: z.number().int().min(1).max(INVOICE_LINK_MAX_DAYS).default(INVOICE_LINK_DEFAULT_DAYS),
  })
  .strict();
export type SendInvoiceInput = z.input<typeof sendInvoiceInputSchema>;
export type SendInvoiceData = z.output<typeof sendInvoiceInputSchema>;

export const invoiceSendResultSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  number: z.string().nullable(),
  channel: invoiceSendChannelSchema,
  status: invoiceSendStatusSchema,
  recipients: z.array(z.string()),
  cc: z.array(z.string()),
  subject: z.string().nullable(),
  /** Phase 1 WhatsApp is click-to-chat: the caller opens this URL (PRD §5.5). */
  whatsappUrl: z.string().nullable(),
  whatsappMessage: z.string().nullable(),
  /** Tokenised, login-free view of the document. */
  viewUrl: z.string(),
  viewUrlExpiresAt: timestampSchema,
  activityId: uuidSchema.nullable(),
  createdAt: timestampSchema,
});
export type InvoiceSendResultDto = z.infer<typeof invoiceSendResultSchema>;

// ---------------------------------------------------------------------------
// The public (login-free) view — GET /api/public/v1/invoices/:token
// ---------------------------------------------------------------------------

export const publicInvoiceLineSchema = z.object({
  description: z.string(),
  sacCode: z.string().nullable(),
  quantityMilli: z.number().int(),
  unit: z.string(),
  unitPriceMinor: minorSchema,
  taxableMinor: minorSchema,
  taxRateBps: z.number().int(),
  totalMinor: minorSchema,
});

export const publicInvoiceTokenParamsSchema = z.object({ token: z.string().min(16).max(512) });

/** GET /api/public/v1/invoices/:token → 200. A bad, tampered or expired token is a 404. */
export const publicInvoiceViewSchema = z.object({
  number: z.string(),
  docType: invoiceDocTypeSchema,
  docTypeLabel: z.string(),
  status: invoiceStatusSchema,
  supplierName: z.string(),
  supplierGstin: z.string().nullable(),
  buyerName: z.string(),
  buyerGstin: z.string().nullable(),
  placeOfSupply: z.string().nullable(),
  invoiceDate: dateOnlySchema,
  dueDate: dateOnlySchema.nullable(),
  currency: currencyCodeSchema,
  taxableMinor: minorSchema,
  cgstMinor: minorSchema,
  sgstMinor: minorSchema,
  igstMinor: minorSchema,
  roundOffMinor: minorSchema,
  totalMinor: minorSchema,
  balanceMinor: minorSchema,
  amountInWords: z.string().nullable(),
  reverseCharge: z.boolean(),
  exportEndorsement: z.string().nullable(),
  lines: z.array(publicInvoiceLineSchema),
  /** `upi://pay?...` with the balance pre-filled, or null when UPI does not apply. */
  upiUri: z.string().nullable(),
  bankAccountName: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  bankIfsc: z.string().nullable(),
  bankName: z.string().nullable(),
  upiId: z.string().nullable(),
  /** Same token, PDF bytes. Null while the render has not finished. */
  pdfUrl: z.string().nullable(),
  viewedAt: timestampSchema.nullable(),
});
export type PublicInvoiceViewDto = z.infer<typeof publicInvoiceViewSchema>;

// ---------------------------------------------------------------------------
// Recurring retainers (US-4.3)
// ---------------------------------------------------------------------------

/** "2026-10" — the calendar month the retainer run is reviewed in. */
export const periodMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Expected YYYY-MM");

export const RETAINER_SKIP_REASONS = [
  "already_invoiced",
  "client_paused",
  "client_churned",
  "engagement_ended",
  "not_a_retainer",
  "no_fee",
  "outside_window",
] as const;
export type RetainerSkipReason = (typeof RETAINER_SKIP_REASONS)[number];

export const retainerCandidateSchema = z.object({
  engagementId: uuidSchema,
  engagementName: z.string(),
  clientId: uuidSchema,
  clientName: z.string(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  periodLabel: z.string(),
  invoiceDate: dateOnlySchema,
  currency: currencyCodeSchema,
  feeMinor: minorSchema,
  outcome: z.enum(["created", "skipped"]),
  reason: z.string().nullable(),
  invoiceId: uuidSchema.nullable(),
});
export type RetainerCandidateDto = z.infer<typeof retainerCandidateSchema>;

/** POST /api/v1/invoices/retainer-run/generate (perm `invoices:write`) → 200. */
export const generateRetainerDraftsInputSchema = z
  .object({
    /** Defaults to the workspace's today. */
    asOf: dateOnlySchema.optional(),
    /** Raise the draft this many days before the invoice day (US-4.3 default 3). */
    leadDays: z.number().int().min(0).max(31).default(3),
  })
  .strict();
export type GenerateRetainerDraftsInput = z.input<typeof generateRetainerDraftsInputSchema>;
export type GenerateRetainerDraftsData = z.output<typeof generateRetainerDraftsInputSchema>;

export const retainerGenerationSchema = z.object({
  asOf: dateOnlySchema,
  leadDays: z.number().int(),
  created: z.number().int(),
  skipped: z.number().int(),
  candidates: z.array(retainerCandidateSchema),
});
export type RetainerGenerationDto = z.infer<typeof retainerGenerationSchema>;

export const retainerRunQuerySchema = z.object({ period: periodMonthSchema.optional() });
export type RetainerRunQuery = z.infer<typeof retainerRunQuerySchema>;

export const retainerRunItemSchema = z.object({
  invoiceId: uuidSchema,
  number: z.string().nullable(),
  status: invoiceStatusSchema,
  statusLabel: z.string(),
  clientId: uuidSchema,
  clientName: z.string(),
  engagementId: uuidSchema.nullable(),
  periodStart: dateOnlySchema.nullable(),
  periodEnd: dateOnlySchema.nullable(),
  periodLabel: z.string().nullable(),
  invoiceDate: dateOnlySchema,
  dueDate: dateOnlySchema.nullable(),
  currency: currencyCodeSchema,
  totalMinor: minorSchema.nullable(),
  balanceMinor: minorSchema.nullable(),
  sentAt: timestampSchema.nullable(),
  pdfReady: z.boolean(),
});
export type RetainerRunItemDto = z.infer<typeof retainerRunItemSchema>;

export const retainerRunTotalsSchema = z.object({
  currency: currencyCodeSchema,
  count: z.number().int(),
  draftCount: z.number().int(),
  issuedCount: z.number().int(),
  totalMinor: minorSchema.nullable(),
});

/** GET /api/v1/invoices/retainer-run (perm `invoices:read`) → 200 — Finance's monthly screen. */
export const retainerRunSchema = z.object({
  period: periodMonthSchema,
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  items: z.array(retainerRunItemSchema),
  totals: z.array(retainerRunTotalsSchema),
});
export type RetainerRunDto = z.infer<typeof retainerRunSchema>;

/** POST /api/v1/invoices/retainer-run/issue (perm `invoices:send`) → 200. Issues, and optionally sends, in bulk. */
export const issueRetainerRunInputSchema = z
  .object({
    invoiceIds: z.array(uuidSchema).min(1).max(200),
    send: z.boolean().default(false),
  })
  .strict();
export type IssueRetainerRunInput = z.input<typeof issueRetainerRunInputSchema>;
export type IssueRetainerRunData = z.output<typeof issueRetainerRunInputSchema>;

export const bulkIssueResultSchema = z.object({
  issued: z.number().int(),
  sent: z.number().int(),
  failures: z.array(z.object({ invoiceId: uuidSchema, message: z.string() })),
  invoiceIds: z.array(uuidSchema),
});
export type BulkIssueResultDto = z.infer<typeof bulkIssueResultSchema>;

// ---------------------------------------------------------------------------
// Exports for the CA (US-4.8)
// ---------------------------------------------------------------------------

export const INVOICE_EXPORT_FORMATS = ["documents", "gstr1", "tally"] as const;
export type InvoiceExportFormat = (typeof INVOICE_EXPORT_FORMATS)[number];
export const invoiceExportFormatSchema = z.enum(INVOICE_EXPORT_FORMATS);

/** GET /api/v1/invoices/exports (perms `invoices:read` + `financials:view`) → 200. */
export const invoiceExportQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  format: invoiceExportFormatSchema.optional(),
});
export type InvoiceExportQuery = z.infer<typeof invoiceExportQuerySchema>;

export const invoiceExportSheetSchema = z.object({
  /** "B2B", "CDNR", "Invoices"… — the tab name in the workbook. */
  name: z.string(),
  filename: z.string(),
  contentType: z.string(),
  columns: z.array(z.string()),
  rowCount: z.number().int(),
  /** RFC-4180 CSV (or XML for the Tally voucher file), ready to save as-is. */
  content: z.string(),
});
export type InvoiceExportSheetDto = z.infer<typeof invoiceExportSheetSchema>;

/**
 * US-4.8: "Export totals must equal report totals; this is checked automatically
 * before download." `ok` false never reaches the caller — the route refuses.
 */
export const invoiceExportReconciliationSchema = z.object({
  documentCount: z.number().int(),
  rowCount: z.number().int(),
  reportTaxableMinor: minorSchema,
  exportTaxableMinor: minorSchema,
  reportTaxMinor: minorSchema,
  exportTaxMinor: minorSchema,
  reportTotalMinor: minorSchema,
  exportTotalMinor: minorSchema,
  ok: z.boolean(),
});
export type InvoiceExportReconciliationDto = z.infer<typeof invoiceExportReconciliationSchema>;

export const invoiceExportSchema = z.object({
  format: invoiceExportFormatSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  currency: currencyCodeSchema,
  generatedAt: timestampSchema,
  sheets: z.array(invoiceExportSheetSchema),
  reconciliation: invoiceExportReconciliationSchema,
});
export type InvoiceExportDto = z.infer<typeof invoiceExportSchema>;

/** The ledger names a Tally import maps our documents onto (US-4.8). */
export const invoiceExportSettingsSchema = z.object({
  tallyCompanyName: z.string().nullable(),
  salesLedger: z.string(),
  creditNoteLedger: z.string(),
  receiptLedger: z.string(),
  bankLedger: z.string(),
  cgstLedger: z.string(),
  sgstLedger: z.string(),
  igstLedger: z.string(),
  cessLedger: z.string(),
  roundOffLedger: z.string(),
  updatedAt: timestampSchema,
});
export type InvoiceExportSettingsDto = z.infer<typeof invoiceExportSettingsSchema>;

const ledgerName = z.string().trim().min(1).max(120);

/** PATCH /api/v1/invoices/export-settings (perm `org:manage`) → 200. */
export const updateInvoiceExportSettingsInputSchema = z
  .object({
    tallyCompanyName: z.string().trim().max(120).nullable(),
    salesLedger: ledgerName,
    creditNoteLedger: ledgerName,
    receiptLedger: ledgerName,
    bankLedger: ledgerName,
    cgstLedger: ledgerName,
    sgstLedger: ledgerName,
    igstLedger: ledgerName,
    cessLedger: ledgerName,
    roundOffLedger: ledgerName,
  })
  .partial()
  .strict();
export type UpdateInvoiceExportSettingsInput = z.input<typeof updateInvoiceExportSettingsInputSchema>;
export type UpdateInvoiceExportSettingsData = z.output<typeof updateInvoiceExportSettingsInputSchema>;

// ---------------------------------------------------------------------------
// Pure helpers shared by the API and the web app
// ---------------------------------------------------------------------------

/**
 * The UPI deep link of PRD §5.4, with the amount pre-filled:
 * `upi://pay?pa=<VPA>&pn=<name>&am=<amount>&cu=INR&tn=<invoice no>`.
 * Returns null unless the money is INR, positive, and a VPA is configured.
 */
export function upiPaymentUri(input: {
  upiId: string | null | undefined;
  payeeName: string;
  amountMinor: number;
  currency: string;
  note?: string | null;
}): string | null {
  const vpa = input.upiId?.trim();
  if (!vpa || input.currency !== "INR" || input.amountMinor <= 0) return null;
  const amount = (input.amountMinor / 100).toFixed(2);
  const params = new URLSearchParams({ pa: vpa, pn: input.payeeName.slice(0, 50), am: amount, cu: "INR" });
  if (input.note) params.set("tn", input.note.slice(0, 50));
  return `upi://pay?${params.toString()}`;
}

/** Phase-1 WhatsApp: a `wa.me` click-to-chat URL with the message pre-rendered. */
export function whatsAppChatUrl(phone: string, message: string): string | null {
  const digits = phone.replace(/[^0-9]/g, "");
  if (digits.length < 8) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

/** First and last calendar day of a "YYYY-MM" period. */
export function monthBounds(period: string): { start: string; end: string } {
  const [year, month] = period.split("-").map(Number);
  const y = year ?? 1970;
  const m = month ?? 1;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { start: `${period}-01`, end: `${period}-${String(last).padStart(2, "0")}` };
}

/**
 * The GSTR-1 table a document belongs in (B2B, B2CL, B2CS, EXP, CDNR, CDNUR).
 *
 * A refund voucher (Rule 51) is not an outward supply: it gives back an advance
 * declared in Table 11A. It goes in Table 9B, "Debit Notes/Credit Notes/Refund
 * voucher" — CDNR for a registered buyer, CDNUR otherwise. Credit and debit
 * notes stay in CDNR whoever the buyer is (their unregistered-buyer routing is
 * a separate, known gap).
 */
export function gstr1Table(input: { docType: string; supplyType: string; buyerGstin: string | null; totalMinor: number; interState: boolean }): string {
  if (input.docType === "RFV") return input.buyerGstin ? "CDNR" : "CDNUR";
  if (input.docType === "CRN" || input.docType === "DBN") return "CDNR";
  if (input.supplyType === "export") return "EXP";
  if (input.buyerGstin) return "B2B";
  // B2CL: unregistered, inter-state, invoice value above the ₹2.5 lakh threshold.
  if (input.interState && input.totalMinor > 25_000_000) return "B2CL";
  return "B2CS";
}
